<template>
	<!-- 
		# MainCard

		V.1.3

		Limpieza general y optimizaciones menores

		V.1.2

		Buscador automático
		
		V.1.1

		Ahora la configuración de origen de datos y origen de índice, 
		junto a la coniguración de campos, se define fuera del objeto de exportación

		V.1.0

		Para la integración de imágenes requiere 
		**img-input-autoload** configurado en elements y
		el controlador UiController.php y UiModel.php en la API Base. **Además hace uso del 
		paginador para javascript implementado en PaginateController.php**

		Para la actualización de datos hace uso de StorageController.php 

		@Author Daniel Lepe 2021
	-->
	<b-row id="AdministracionMonitorComprasVentas">
		<b-col md="3" lg="2" id="controlPanelHolder" class=" no-print">
			<b-card bg-variant="dark">
				<div slot="header">Reporte de utilidades</div>
				<div class="reporte-utilidades">
					<div id="inicioDelReporte">
						<label for="inicio-del-reporte">Inicio del reporte</label>
						<b-form-datepicker
							id="inicio-del-reporte"
							:min="min"
							:max="max"
							v-model="form.inicio"
							class="mb-2"
						></b-form-datepicker>
					</div>
					<div id="finDelReporte">
						<label for="fin-del-reporte">Fin del reporte</label>
						<b-form-datepicker
							id="fin-del-reporte"
							:min="minEnd"
							:max="maxEnd"
							v-model="form.fin"
							:disabled="!form.inicio"
							class="mb-2"
						></b-form-datepicker>
					</div>
					<div id="filtraPorCategoria">
						<label for="filtra-por-categoria">Filtra por categoría</label>
						<b-form-select
							id="filtra-por-categoria"
							:options="catsMain"
							v-model="form.categoria"
							class="mb-2"
						></b-form-select>
					</div>
					<!-- 
					<div>
						<label for="fin-del-reporte">Filtra por producto</label>
						<b-form-select id="fin-del-reporte"  :options="['uno', 'dos']"  v-model="form.elemento" class="mb-2"></b-form-select>
					</div> 
					-->
				</div>
				<b-row class="mt-3 pt-1 no-print" align-h="end">
					<b-col>
						<b-button block variant="primary" @click="data.page = 1; refresh()">Aplicar filtro</b-button>
					</b-col>
				</b-row>
			</b-card>
		</b-col>

		<b-col md="9" lg="10">
			<b-card no-body>
				<!-- CARD HEADER -->
				<b-row class="no-print" slot="header">
					<b-col cols="6">
						<div tabindex="-1" role="group">
							<div role="group" class="input-group">
								<input
									type="text"
									v-model="term"
									@keyup.enter="refresh()"
									:placeholder="labelsCfg.searchPlaceholder"
									class="form-control"
								/>
								<div class="input-group-append" v-if="!!term">
									<b-button type="button" variant="light" @click="term = null; refresh()">
										<i class="fa fa-times"></i>
									</b-button>
									<b-button type="button" :variant="variant" @click="refresh()">
										<i class="fa fa-search"></i>
									</b-button>
								</div>
							</div>
						</div>
					</b-col>
					<b-col cols="6" class="text-right">
						<b-button-group class="mt-1" size="sm">
							<b-button @click="togglePriceChanges()">
								<i class="fa fa-square" key="square" v-if="simplifyForExport"></i> 
								<i class="fa fa-check-square" key="square-not" v-else></i> 
								Mínimos/máximos
							</b-button>
							<b-button :variant="variant" @click="downloadXLSX()">
								<i class="fa fa-download"></i> XLSX
							</b-button>
						</b-button-group>
					</b-col>
				</b-row>
				<!-- CARD HEADER -->

				<spinner v-if="isLoading" />

				<!-- DATA TEMPLATE -->
				<template v-else-if="parseInt(data.count)">
					<b-table
						id="data-table"
						:fields="fields"
						responsive
						striped
						sticky-header="true"
						:no-border-collapse="true"
						class="mb-0"
						:items="items"
					>
						<template #custom-foot>
							<b-tr>
								<b-td colspan="4" class="text-right">
									<b>Totales:</b>
								</b-td>
								<b-td class="text-center costo-cell">
									<b>{{outSum | currency}}</b>
								</b-td>
								<b-td class="text-center costo-cell">--</b-td>
								<b-td class="text-center costo-cell">--</b-td>
								<b-td class="text-center costo-cell">
									<b>{{inSum | currency}}</b>
								</b-td>
								<b-td class="text-center costo-cell">
									<strong
										:class="{bad: ((inSum - outSum) < 0), good: ((inSum - outSum) > 0)}"
									>{{(inSum - outSum) | currency}}</strong>
								</b-td>
							</b-tr>
						</template>

						<!-- START TEMPLATES BASICOS DE DATOS -->
						<template #cell(nombre)="data">
							<div class="text-left text-nowrap">
								<strong>
									{{data.item.nombre}}
								</strong>
								{{data.item.tipopresentacion}}
								{{data.item.presentacion}}
								{{data.item.unidad}}
							</div>
						</template>

						<!-- START TEMPLATES BASICOS DE DATOS -->
						<template #cell(marca)="data">
							<strong class="text-nowrap">
								{{data.value}}
							</strong>
						</template>

						<!-- CANTIDAD DE COMPRA -->
						<template #cell(cantidad)="data">
							<div class="cantidad" v-b-popover.hover.top="'Cantidad comprada'">{{data.value}}</div>
						</template>

						<template #cell(cant_venta)="data">
							<div class="cantidad" v-b-popover.hover.top="'Cantidad vendida'" v-if="data.value">{{data.value}}</div>
							<div class="cantidad" v-else>0</div>
						</template>

						<!-- COSTOS -->
						<template #cell(avgCosto)="data">
							<div class="costo-cell">
								<span v-if="parseFloat(data.item.minCosto) == parseFloat(data.item.maxCosto)">
									<strong>{{data.value | currency}}</strong>
								</span>
								<span v-else>
									<small v-b-popover.hover.top="'Costo mínimo de compra'" v-if="!simplifyForExport">({{data.item.minCosto | currency}})</small>
									<strong>{{data.value | currency}}</strong>
									<small v-b-popover.hover.top="'Costo máximo de compra'" v-if="!simplifyForExport">({{data.item.maxCosto | currency}})</small>
								</span>
							</div>
						</template>

						<template #cell(ventaAvgPrecio)="data">
							<div class="costo-cell">
								<span v-if="parseFloat(data.item.venta.minPrecio) == parseFloat(data.item.venta.maxPrecio)">
									<strong>{{data.item.ventaAvgPrecio | currency}}</strong>
								</span>
								<span v-else-if="!!data.item.ventaAvgPrecio">
									<small v-b-popover.hover.top="'Precio mínimo de venta'" v-if="!simplifyForExport">({{data.item.venta.minPrecio | currency}})</small>
									<strong>{{data.item.ventaAvgPrecio | currency}}</strong>
									<small v-b-popover.hover.top="'Precio máximo de venta'" v-if="!simplifyForExport">({{data.item.venta.maxPrecio | currency}})</small>
								</span>
								<span v-else>
									<small>--</small>
								</span>
							</div>
						</template>

						<template #cell(total)="data">
							<div class="costo-cell">
								<span>
									<strong>{{data.value | currency}}</strong>
								</span>
							</div>
						</template>

						<template #cell(total_venta)="data">
							<div class="costo-cell">
								<span v-if="data.value">
									<strong>{{data.value | currency}}</strong>
								</span>
								<span v-else>{{0 | currency}}</span>
							</div>
						</template>

						<template #cell(diferencia)="data">
							<div class="costo-cell">
								<strong :class="{good: (data.item.total < data.item.venta.total), bad: (data.item.total > data.item.venta.total), tie: (data.item.total == data.item.venta.total)}">
									{{data.value | currency}}
								</strong>
							</div>
						</template>
						<!-- END TEMPLATES BASICOS DE DATOS -->

						<!-- START OF AVATAR SAMPLES -->
						<template #cell(imagenes_id_agenda)="data">
							<api-img :comp="data.value" :rowed="true" />
						</template>
						<!-- END OF AVATAR SAMPLES -->

						<!-- START OF BOOLEAN SAMPLE -->
						<template #cell(disponible)="data">
							<div class="align-center">
								<!-- <b-button size="sm" variant="primary" v-if="data.value == '1'"> -->
								<i v-if="data.value == '1'" class="fa text-primary fa-check"></i>
								<!-- </b-button> -->
								<!-- <b-button size="sm" v-else> -->
								<i v-else class="fa fa-minus"></i>
								<!-- </b-button> -->
							</div>
						</template>
						<!-- END OF BOOLEAN SAMPLE -->

						<!-- START OF TOOLING -->
						<template #cell(_)="data">
							<b-button @click="setupItemInFormAndOpenModal(data.item)" size="sm" :variant="variant">
								<i class="fa fa-fw fa-cog"></i>
							</b-button>
						</template>
						<!-- END OF TOOLING -->
					</b-table>

					<b-row slot="footer" align-v="center">
						<b-col>
							<strong>Se muestran {{items.length}} registros de un total de {{data.count}}.</strong>
							<span
								v-if="Math.ceil(data.count / data.per_page) > 1"
							>(Página {{data.page}} de {{Math.ceil(data.count / data.per_page)}})</span>
						</b-col>
						<b-col class="no-print" v-if="Math.ceil(data.count / data.per_page) > 1">
							<b-pagination
								class="mt-3"
								v-model="data.page"
								:total-rows="data.count"
								:per-page="data.per_page"
								size="sm"
								align="right"
								prev-text="Anterior"
								next-text="Siguiente"
							/>
						</b-col>
					</b-row>
				</template>
				<!-- DATA TEMPLATE -->

				<!-- EMPTY JUMBOTRON -->
				<b-jumbotron class="mb-0" v-else>
					<template #header>{{labelsCfg.emptyHeader}}</template>
					<template #lead>{{labelsCfg.emptyLead}}</template>
				</b-jumbotron>
				<!-- EMPTY JUMBOTRON -->

				<!-- MAIN MODAL -->
				<b-modal id="main-card-modal" cancel-disabled ok-disabled scrollable @hidden="hidden">
					<b-form @submit="onSubmit"></b-form>

					<template v-slot:modal-footer="{ cancel }">
						<b-button
							size="sm"
							variant="outline-danger"
							@click="tryToDelete(form.id)"
							v-if="!isProcessing && !!form.id"
						>
							<i class="fa fa-trash"></i>
							{{labelsCfg.btnDelete}}
						</b-button>

						<b-button size="sm" @click="cancel()" v-if="!isProcessing">{{labelsCfg.btnCancel}}</b-button>

						<!-- Button with custom close trigger value -->
						<b-button
							size="sm"
							:variant="variant"
							@click="store()"
							v-if="!isProcessing"
						>{{labelsCfg.btnApply}}</b-button>

						<!-- Button with custom close trigger value -->
						<b-button size="sm" :variant="variant" disabled v-if="isProcessing">
							<span key="spin">
								<i class="fa fa-cog fa-spin"></i>
							</span>
							{{labelsCfg.btnIsProcessing}}
						</b-button>
					</template>
				</b-modal>
				<!-- MAIN MODAL -->
			</b-card>
		</b-col>
	</b-row>
</template>

<script>
import axios from "axios";
import _ from "lodash";
import swal from "sweetalert";
import fkSelect from "@/views/elements/fk-select.vue";
import imgInputAutoload from "@/views/elements/img-input-autoload.vue";
import md5 from "js-md5";
import moment from "moment";
import { mapActions, mapGetters } from "vuex";
import XLSX from 'xlsx'

let labelsCfg = {
	emptyHeader: "Lista vacía",
	emptyLead: "",
	searchPlaceholder: "Buscador...",
	newLabel: false,
	btnDelete: "Eliminar",
	btnCancel: "Cancelar",
	btnApply: "Aplicar",
	btnIsProcessing: "Procesando...",
};

let indexSource = "monitorcompraventa/filter", // Para indexar
	dataSource = "define/source/agentes_servicio"; // Para crear, actualizar y borrar
// dataSource = 'usuario/createagenda'; // Para crear, actualizar y borrar

let formArchetype = {
	inicio: moment().add(-8, "days").toDate(),
	fin: moment().add(-1, "days").toDate(),
	categoria: null,
	elemento: null,
};

let fields = [
	// DEJAR VACÍO PARA MOSTRAR TODO
	{
		key: "nombre",
		label: "PRODUCTO",
		stickyColumn: true,
		isRowHeader: true, 
		sortable: true,
	},
	{
		key: "marca",
		sortable: true,
	},

	{ key: "avgCosto", label: "COSTO", sortable: true },
	{ key: "cantidad", label: "COMPRADO", sortable: true },
	{ key: "total", label: "INVERSIÓN", sortable: true },

	{ key: "ventaAvgPrecio", label: "PRECIO", sortable: true },
	{ key: "cant_venta", label: "VENDIDO", sortable: true },
	{ key: "total_venta", label: "VENTA", sortable: true },

	{ key: "diferencia", label: "DIFERENCIA", sortable: true },
];

export default {
	name: "AdministracionMonitorComprasVentas",
	props: {},
	components: {
		fkSelect,
		imgInputAutoload,
	},
	computed: {
		...mapGetters(["uiGetCategoriasFiltro", "isLogged"]),
		outSum() {
			let sum = 0;
			_.forEach(this.items, (item) => {
				sum += item.total;
			});
			return sum;
		},
		inSum() {
			let sum = 0;
			_.forEach(this.items, (item) => {
				sum += item.venta.total;
			});
			return sum;
		},
		catsMain() {
			let ret = [
				{
					value: "*",
					text: "-- Sin filtrar --",
				},
			];
			_.forEach(this.uiGetCategoriasFiltro, (cat) => {
				ret.push({
					value: cat.cat_id_pk,
					text: cat.cat,
				});
			});
			return _.uniqBy(ret, "value");
		},
		items() {
			return _.map(this.data.rows, (item) => {
				item.ventaAvgPrecio = item.venta.avgPrecio
				item.total_venta = item.venta.total
				item.cant_venta = item.venta.cantidad
				item.diferencia = (item.venta.total - item.total)
				item._ = " ";
				return item;
			});
		},
		dataMD5() {
			return md5(JSON.stringify(this.form));
		},
		// COMPUTA LA URI
		sourceURI() {
			let init = `${this.indexSource}/${moment(this.form.inicio).format(
				"YYYY-MM-DD"
			)}/${moment(this.form.fin).format("YYYY-MM-DD")}/${
				this.data.per_page
			}/${this.data.page}`;
			if (!!this.term) {
				init += "/" + this.term;
			} else if (!this.term || this.term == "s") {
				init += "/@";
			}
			if (!!this.form.categoria) {
				init += "/" + this.form.categoria;
			}
			return init;
		},
		minEnd() {
			return moment(this.form.inicio, "YYYY-MM-DD")
				.add(0, "day")
				.toDate();
		},
		maxEnd() {
			return moment(this.form.inicio, "YYYY-MM-DD")
				.add(30, "day")
				.toDate();
		},
	},
	data: () => {
		const now = new Date();
		const today = new Date(
			now.getFullYear(),
			now.getMonth(),
			now.getDate()
		);

		// 15th two months prior
		const minDate = new Date(today);
		minDate.setFullYear(minDate.getFullYear() - 1);

		// 15th in two months
		const maxDate = new Date(today);
		maxDate.setDate(maxDate.getDate() + 6);

		return {
			min: minDate,
			max: maxDate,
			isLoading: false,
			isProcessing: false,
			term: null,
			variant: "primary",
			dataSource,
			indexSource,
			fields,
			simplifyForExport: true,
			labelsCfg: _.cloneDeep(labelsCfg),
			form: _.cloneDeep(formArchetype),
			data: {
				rows: [],
				count: 0,
				per_page: 1500,
				page: 1,
			},
		};
	},
	methods: {
		...mapActions(["updateCategoriasFiltro"]),

		togglePriceChanges(){
			this.simplifyForExport = !this.simplifyForExport
		},

		// DOWNLOAD XLSX
		async downloadXLSX(){

			console.log('writing')
			// this.simplifyForExport = true
			let ret = await this._extract_xslx()
			// this.simplifyForExport = false
			console.log('writing done')
			return ret
		},

		async _extract_xslx(){
			let type = 'xlsx'
			var elt = document.getElementById('data-table');
			let ret = null
			var wb = XLSX.utils.table_to_book(elt, {sheet:"Reporte compra - ventas"});
			ret = await XLSX.writeFile(wb, ('Reporte compra - ventas.' + (type || 'xlsx')));
			return ret
		},

		// PREGUNTA Y CONTINÚA CON EL BORRADO DEL REGISTRO
		tryToDelete(itemID) {
			if (!!itemID) {
				swal({
					text: "¿Estás seguro que deseas eliminar éste registro?",
					icon: "info",
					buttons: {
						cancel: "Cancelar",
						proceed: "Si",
					},
				}).then((selected) => {
					if (selected == "proceed") {
						// HE SAID YES!
						this.isProcessing = true;
						axios
							.delete(this.dataSource + "/" + itemID)
							.then((response) => {
								if (response.status) {
									// this.$router.push({name: 'Levantamiento de inventario', params: {id: response.data.id}})
									this.loadUp().then(() => {
										this.$bvModal.hide("main-card-modal");
										this.isProcessing = false;
									});
								} else {
									this.isProcessing = false;
								}
							});
					}
				});
			} else {
				swal({
					text: "Para poder eliminar un registro, éste debe especifcar un ID de eliminación.",
				});
			}
		},

		// PREPARA LO QUE TENGAS QUE PREPARAR PARA SUBIR AL SERVIDIOR, COMO AGREGAR VALIDACIONES, DATOS O PROPIEDADES DINÁMICAS, ETC.
		// DEVULEVE FALSO PARA DETENER EL ENVÍO
		getDataForSubmit() {
			let data = _.cloneDeep(this.form);
			(data.usuarios_id = this.usuariosId),
				_.forEach(
					[
						"fecha_agenda",
						"usuarios_vehiculos_id",
						"sucursales_id",
						"tipo_servicio",
					],
					(keyRequired) => {
						if (typeof data == "object" && !data[keyRequired]) {
							swal({
								text: "Asegúrate de llenar todos los campos con *.",
							});
							data = false;
						}
					}
				);
			return data;
		},
		cleanFormAndOpenModal() {
			this.form = _.cloneDeep(formArchetype);
			this.$bvModal.show("main-card-modal");
		},
		setupItemInFormAndOpenModal(item) {
			this.form = _.cloneDeep(item);
			this.$bvModal.show("main-card-modal");
		},
		refresh() {
			this.isLoading = true;
			this.loadUp().then(() => {
				this.isLoading = false;
			});
		},
		loadUp() {
			return new Promise((success) => {
				axios.get(this.sourceURI).then((response) => {
					if (typeof response.data.rows != undefined) {
						this.data.rows = response.data.rows;
						this.data.count = response.data.count;
						success();
					}
				});
			});
		},
		hidden() {
			this.form = _.cloneDeep(formArchetype);
		},
		store() {
			let data2send = this.getDataForSubmit();
			if (!!data2send) {
				this.isProcessing = true;
				axios
					.post(
						this.dataSource +
							(!!this.form.id ? "/" + this.form.id : ""),
						data2send
					)
					.then((response) => {
						if (response.status) {
							// this.$router.push({name: 'Levantamiento de inventario', params: {id: response.data.id}})
							this.loadUp().then(() => {
								this.$bvModal.hide("main-card-modal");
								this.isProcessing = false;
							});
						} else {
							this.isProcessing = false;
						}
					});
			}
		},
		onSubmit($evnt) {
			$evnt.preventDefault();
			this.store();
		},
	},
	async beforeMount() {
		this.isLoading = true;

		let init = async () => {
			// SIEMPRE ACTUALIZAMOS LA LISTA DE CATEGORÍAS, PERO SOLO ESPERAMOS LA UI CUANDO NO HAYA UNA PRECARGA.
			if (!this.uiGetCategoriasFiltro.length)
				await this.updateCategoriasFiltro();
			await this.loadUp();
			this.isLoading = false;
		}

		if(this.isLogged){
			init()
		} else {
			setTimeout(init, 750)
		}
	},
	watch: {
		"data.page"() {
			this.isLoading = true;
			this.loadUp().then(() => {
				this.isLoading = false;
			});
		},
	},
};
</script>

<style lang="less">
#AdministracionMonitorComprasVentas {
	.sr-only {
		display: none;
	}
	@media print {
		.no-print {
			display: none;
		}
	}
	TH {
		text-align: center;
	}
	.costo-cell {
		text-align: center;

		STRONG {
			margin: 0 0.5em;
		}

		.bad {
			color: rgb(234, 80, 29);
		}
		.good {
			color: rgb(91, 151, 0);
		}
		.tie {
			color: rgb(96, 96, 96);
		}
	}
	SMALL {
		opacity: 0.3333;
	}
	.cantidad {
		text-align: center;
		font-weight: bold;
	}

	.b-table-sticky-header {
		/* max-height: 300px; */
		max-height: calc(100vh - 306px);
	}
}
</style>